import { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';

export interface DataItem {
  id: string;
  title: string;
  description: string;
  type: 'file' | 'text';
  fileUrl?: string;
  fileName?: string;
  fileType?: string;
  textContent?: string;
  uploadedAt: string;
  clientId?: string;
  projectNumber?: string;
  projectName?: string;
  isPlaceholder?: boolean;
}

interface UploadSectionProps {
  onUploadComplete: () => void;
}

export default function UploadSection({ onUploadComplete }: UploadSectionProps) {
  const { getClients } = useAuth();
  const clients = getClients();

  const [uploadType, setUploadType] = useState<'file' | 'text'>('file');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [textContent, setTextContent] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [projectNumber, setProjectNumber] = useState('');
  const [projectName, setProjectName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setError('');
    }
  };

  const validateForm = (): boolean => {
    if (!title.trim()) {
      setError('Title is required');
      return false;
    }
    if (!selectedClientId) {
      setError('Please select a client');
      return false;
    }
    if (!projectNumber.trim()) {
      setError('Project number is required');
      return false;
    }
    if (!projectName.trim()) {
      setError('Project name is required');
      return false;
    }
    if (uploadType === 'file' && !selectedFile) {
      setError('Please select a file to upload');
      return false;
    }
    if (uploadType === 'text' && !textContent.trim()) {
      setError('Text content is required');
      return false;
    }
    return true;
  };

  const handleUpload = async () => {
    setError('');
    setSuccess('');

    if (!validateForm()) return;

    setUploading(true);

    try {
      const newItem: DataItem = {
        id: `data_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        title: title.trim(),
        description: description.trim(),
        type: uploadType,
        uploadedAt: new Date().toISOString(),
        clientId: selectedClientId,
        projectNumber: projectNumber.trim(),
        projectName: projectName.trim()
      };

      if (uploadType === 'file' && selectedFile) {
        // Convert file to base64 for storage
        const reader = new FileReader();
        reader.onload = (e) => {
          const base64 = e.target?.result as string;
          newItem.fileUrl = base64;
          newItem.fileName = selectedFile.name;
          newItem.fileType = selectedFile.type;

          saveToStorage(newItem);
        };
        reader.readAsDataURL(selectedFile);
      } else if (uploadType === 'text') {
        newItem.textContent = textContent.trim();
        saveToStorage(newItem);
      }
    } catch (err) {
      setError('Upload failed. Please try again.');
      setUploading(false);
    }
  };

  const saveToStorage = (item: DataItem) => {
    try {
      const existingData = localStorage.getItem('admin_data_items');
      const dataItems: DataItem[] = existingData ? JSON.parse(existingData) : [];
      dataItems.push(item);
      localStorage.setItem('admin_data_items', JSON.stringify(dataItems));

      setSuccess('Upload successful!');
      resetForm();
      onUploadComplete();
    } catch (err) {
      setError('Failed to save data. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setSelectedFile(null);
    setTextContent('');
    setSelectedClientId('');
    setProjectNumber('');
    setProjectName('');
    setUploadType('file');
    
    // Reset file input
    const fileInput = document.getElementById('file-upload') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-6">Upload Data for Client</h2>

      {/* Client Selection */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Client <span className="text-red-500">*</span>
        </label>
        {clients.length === 0 ? (
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              <i className="ri-alert-line mr-2"></i>
              No clients available. Please create a client account first in the "Client Accounts" tab.
            </p>
          </div>
        ) : (
          <select
            value={selectedClientId}
            onChange={(e) => setSelectedClientId(e.target.value)}
            className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none cursor-pointer"
          >
            <option value="">Choose a client...</option>
            {clients.filter(c => c.isActive).map((client) => (
              <option key={client.id} value={client.id}>
                {client.name} ({client.username})
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Project Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Project Number <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={projectNumber}
            onChange={(e) => setProjectNumber(e.target.value)}
            placeholder="e.g., PRJ-2024-001"
            className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Project Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            placeholder="e.g., Q1 Procurement Initiative"
            className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none"
          />
        </div>
      </div>

      {/* Upload Type Toggle */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">Upload Type</label>
        <div className="flex gap-3">
          <button
            onClick={() => setUploadType('file')}
            className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all cursor-pointer whitespace-nowrap ${
              uploadType === 'file'
                ? 'border-gray-900 bg-gray-900 text-white'
                : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
            }`}
          >
            <i className="ri-file-upload-line text-lg mr-2"></i>
            File Upload
          </button>
          <button
            onClick={() => setUploadType('text')}
            className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all cursor-pointer whitespace-nowrap ${
              uploadType === 'text'
                ? 'border-gray-900 bg-gray-900 text-white'
                : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
            }`}
          >
            <i className="ri-file-text-line text-lg mr-2"></i>
            Text Entry
          </button>
        </div>
      </div>

      {/* Title */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Enter document title"
          className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none"
        />
      </div>

      {/* Description */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Add a brief description (optional)"
          rows={3}
          className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none resize-none"
        />
      </div>

      {/* File Upload */}
      {uploadType === 'file' && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select File <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              id="file-upload"
              type="file"
              onChange={handleFileChange}
              className="hidden"
            />
            <label
              htmlFor="file-upload"
              className="flex items-center justify-center w-full px-4 py-8 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors cursor-pointer bg-gray-50"
            >
              <div className="text-center">
                <i className="ri-upload-cloud-2-line text-4xl text-gray-400 mb-2"></i>
                <p className="text-sm text-gray-600">
                  {selectedFile ? (
                    <span className="font-medium text-gray-900">{selectedFile.name}</span>
                  ) : (
                    <>Click to upload or drag and drop</>
                  )}
                </p>
                <p className="text-xs text-gray-500 mt-1">PDF, DOC, XLS, Images, etc.</p>
              </div>
            </label>
          </div>
        </div>
      )}

      {/* Text Entry */}
      {uploadType === 'text' && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Text Content <span className="text-red-500">*</span>
          </label>
          <textarea
            value={textContent}
            onChange={(e) => setTextContent(e.target.value)}
            placeholder="Enter your text content here..."
            rows={8}
            className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none resize-none"
          />
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">
            <i className="ri-error-warning-line mr-2"></i>
            {error}
          </p>
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-600">
            <i className="ri-checkbox-circle-line mr-2"></i>
            {success}
          </p>
        </div>
      )}

      {/* Upload Button */}
      <button
        onClick={handleUpload}
        disabled={uploading || clients.length === 0}
        className={`w-full px-6 py-3 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer ${
          uploading || clients.length === 0
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : 'bg-gray-900 text-white hover:bg-gray-800'
        }`}
      >
        {uploading ? (
          <>
            <i className="ri-loader-4-line animate-spin mr-2"></i>
            Uploading...
          </>
        ) : (
          <>
            <i className="ri-upload-2-line mr-2"></i>
            Upload Data
          </>
        )}
      </button>
    </div>
  );
}